import React, { useState, useCallback } from "react";
import { connect, useDispatch } from "react-redux";
import { 
  Container, Box, TextField, FormControl, 
  InputLabel, MenuItem, Select, Typography, Button, Avatar 
} from "@mui/material";
import { CameraAlt as CameraIcon } from "@mui/icons-material"; // Icon for upload button
import { handleInputChange } from "../components/utilities/HandleChange";
import { ClientCall } from "../context/allotees/apiClass";

const AlloteeProfile = ({ currentUser }) => {
  const [alloteeProfile, setAlloteeProfile] = useState(currentUser);
  const [profilePicture, setProfilePicture] = useState(null); // State to hold the selected file
  const dispatch = useDispatch();

  // Handle form submission
  const createAllotteeAccount = useCallback(() => {
    const formData = new FormData(); // Create a FormData object for file upload
    Object.keys(alloteeProfile).forEach((key) => {
      formData.append(key, alloteeProfile[key]); // Append all profile data
    });
    if (profilePicture) {
      formData.append("img", profilePicture); // Append the profile picture file
    }

    // Call the API to update the user profile
    ClientCall(
      formData, // Use FormData instead of alloteeProfile
      "post",
      `/update-user/${currentUser.id}`,
      "update-user",
      callBack,
      errCallback,
      dispatch,
      "allotee"
    );
  }, [alloteeProfile, currentUser.id, dispatch, profilePicture]);

  // Handle successful response
  const callBack = (response) => {
    console.log(response.message);
    // Update the Redux state with the new user data (including the image URL)
    dispatch({ type: "UPDATE_USER", payload: response.data });
  };

  // Handle error response
  const errCallback = (error) => console.error(error.message);

  // Handle file input change
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfilePicture(file); // Update the state with the selected file
      setAlloteeProfile((prev) => ({ ...prev, img: URL.createObjectURL(file) })); // Preview the image
    }
  };

  return (
    <Container maxWidth="md">
      <Box component="form" sx={{ p: 4, backgroundColor: "#fff", borderRadius: 2, boxShadow: 2 }}>
        <Typography variant="h5" align="center" gutterBottom>
          Edit Allotee Profile
        </Typography>

        <Box display="flex" flexDirection="column" gap={2}>
          {/* Profile Picture Upload */}
          <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              style={{ display: "none" }}
              id="profile-picture-upload"
            />
            <label htmlFor="profile-picture-upload">
              <Button 
                variant="contained" 
                component="span"
                startIcon={<CameraIcon />}
                sx={{ 
                  backgroundColor: "#45a049", 
                  color: "white !important",
                  "&:hover": { backgroundColor: "#3d8b40" } 
                }}
              >
                Upload Profile Picture
              </Button>
            </label>
            {alloteeProfile.img && (
              <Avatar
                src={alloteeProfile.img}
                alt="Profile Preview"
                sx={{ width: 150, height: 150, mt: 2, border: "2px solid #45a049" }}
              />
            )}
          </Box>

          {/* Owner's Name */}
          <TextField
            label="Owner's Name"
            variant="outlined"
            fullWidth
            name="owners_name"
            value={alloteeProfile.owners_name || ""}
            onChange={(e) => handleInputChange(e, setAlloteeProfile, alloteeProfile)}
          />

          {/* Occupier's Name */}
          <TextField
            label="Occupier's Name"
            variant="outlined"
            fullWidth
            name="occupiers_name"
            value={alloteeProfile.occupiers_name || ""}
            onChange={(e) => handleInputChange(e, setAlloteeProfile, alloteeProfile)}
          />

          {/* Email Address */}
          <TextField
            label="Email Address"
            type="email"
            variant="outlined"
            fullWidth
            name="email"
            value={alloteeProfile.email || ""}
            onChange={(e) => handleInputChange(e, setAlloteeProfile, alloteeProfile)}
          />

          {/* Phone Number */}
          <TextField
            label="Phone Number"
            type="tel"
            variant="outlined"
            fullWidth
            name="phone"
            value={alloteeProfile.phone || ""}
            onChange={(e) => handleInputChange(e, setAlloteeProfile, alloteeProfile)}
          />

          {/* Location */}
          <FormControl variant="outlined" fullWidth>
            <InputLabel>Location</InputLabel>
            <Select
              name="location"
              value={alloteeProfile.location || ""}
              onChange={(e) => handleInputChange(e, setAlloteeProfile, alloteeProfile)}
              label="Location"
            >
              <MenuItem value=""><em>None</em></MenuItem>
              <MenuItem value="meiran">Meiran</MenuItem>
              <MenuItem value="ipaja">Ipaja</MenuItem>
              <MenuItem value="ikorodu">Ikorodu</MenuItem>
              <MenuItem value="surulere">Surulere</MenuItem>
            </Select>
          </FormControl>

          {/* Block Number */}
          <TextField
            label="Block Number"
            type="number"
            variant="outlined"
            fullWidth
            name="block_number"
            value={alloteeProfile.block_number || ""}
            onChange={(e) => handleInputChange(e, setAlloteeProfile, alloteeProfile)}
          />

          {/* Flat Number */}
          <TextField
            label="Flat Number"
            variant="outlined"
            fullWidth
            name="flat_number"
            value={alloteeProfile.flat_number || ""}
            onChange={(e) => handleInputChange(e, setAlloteeProfile, alloteeProfile)}
          />

          {/* Submit Button */}
          <Box mt={3} display="flex" justifyContent="center">
            <Button 
              variant="contained" 
              sx={{ 
                backgroundColor: "#45a049", 
                color: "white !important",
                "&:hover": { backgroundColor: "#3d8b40" } 
              }} 
              onClick={createAllotteeAccount}
            >
              Submit
            </Button>
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

export default connect((state) => ({ currentUser: state.alloteeReducer.currentUser }))(AlloteeProfile);